import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { useGetEventById } from '../../../../../services/react-query/events';
import { Contact } from '../../../../../types/Contact';
import { Employee } from '../../../../../types/Employee';
import { Task } from '../../../../../types/Task';
import StatusPill from '../../../../../uikit/pills/StatusPill';
import { humanizeEventDateTimes, humanizeTimestamp } from '../../../../../utility/dateUtils';
import TaskStatusPill from '../../../../tasks/TaskStatusPill';
import DrawerSection from '../DrawerSection';
import InfoRow from '../InfoRow';
import ModuleDrawerHeader from '../ModuleDrawerHeader';
import { moduleDrawerStyles } from '../moduleDrawerStyles';
import _ from 'lodash';

type Props = { uuid: string };

const CenterSpinner = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
    <CircularProgress size={20} />
  </Box>
);

const EventDrawerView: React.FC<Props> = ({ uuid }) => {
  const { data: event, isLoading } = useGetEventById({ uuid });

  if (isLoading) return <CenterSpinner />;
  if (!event)
    return (
      <Typography variant="body2" color="error">
        Event not found
      </Typography>
    );

  const contacts = event.contacts || [];
  const employees = event.employees || [];
  const tasks = event.tasks || [];

  return (
    <>
      <ModuleDrawerHeader>{_.truncate(event.display_name)}</ModuleDrawerHeader>
      <Box sx={moduleDrawerStyles.content}>
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          {/* Event Information */}
          <DrawerSection title="Event Information">
            <InfoRow label="Name" value={event.display_name || event.name} />
            <InfoRow label="Account" value={event.account_name} />
            <InfoRow label="Event Type" value={event.type_value} />
            <InfoRow label="Event Location" value={event.location_value} />
            <InfoRow label="Date / Time" value={humanizeEventDateTimes(event)} />
            <InfoRow label="Status" value={event.status} rightNode={<StatusPill status={event.status} />} />
          </DrawerSection>

          {/* Contacts */}
          <DrawerSection title="Contacts">
            {contacts.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No contacts found
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {contacts.map((contact: Contact) => (
                  <Box key={contact.uuid}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                      {contact.full_name}
                    </Typography>
                    <InfoRow label="Email" value={contact.email} />
                    <InfoRow label="Phone" value={contact.phone_1} />
                  </Box>
                ))}
              </Box>
            )}
          </DrawerSection>

          {/* Employees */}
          <DrawerSection title="Employees">
            {employees.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No employees found
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {employees.map((employee: Employee) => (
                  <Box key={employee.uuid}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.25 }}>
                      {employee.display_name}
                    </Typography>
                    <InfoRow label="Email" value={employee.email} />
                  </Box>
                ))}
              </Box>
            )}
          </DrawerSection>

          {/* Tasks */}
          <DrawerSection title="Tasks">
            {tasks.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No tasks found
              </Typography>
            ) : (
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {tasks.map((task: Task) => {
                  return (
                    <Box key={task.uuid} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, borderBottom: '1px solid #eee', py: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        title={task.name}
                      >
                        {task.name}
                      </Typography>

                      <InfoRow label="Start" value={humanizeTimestamp(task.start_date)} />
                      <InfoRow label="Due" value={humanizeTimestamp(task.due_date)} />
                      <InfoRow label="Employee" value={task.employee_display_name} />
                      <InfoRow label="Status" value={task.status} rightNode={<TaskStatusPill status={task.status} />} />
                    </Box>
                  );
                })}
              </Box>
            )}
          </DrawerSection>
        </Box>
      </Box>
    </>
  );
};

export default EventDrawerView;
